// Small building blocks for the care team screens (see COMPONENTS.md in the
// design handoff). Styles live in careTeam.css under .ct.
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import PaperPlaceholder from "../PaperPlaceholder";

export const icon = (Icon, size = 18) => (
  <Icon size={size} strokeWidth={2} className="ct-icon" aria-hidden="true" />
);

const STATUS_LABELS = { active: "Active", pending: "Pending", inactive: "Inactive" };

export const StatusBadge = ({ status, label }) => (
  <span className={`ct-badge ct-badge--${status}`}>
    <span className="ct-badge__dot" aria-hidden="true" />
    {label || STATUS_LABELS[status]}
  </span>
);

const TRENDS = {
  up: [TrendingUp, "Improving"],
  flat: [ArrowRight, "Steady"],
  down: [TrendingDown, "Declining"],
};

export const TrendIndicator = ({ trend }) => {
  if (!TRENDS[trend]) {
    return <span className="ct-trend ct-trend--none">—</span>;
  }
  const [Icon, label] = TRENDS[trend];
  return (
    <span className={`ct-trend ct-trend--${trend}`}>
      {icon(Icon)}
      {label}
    </span>
  );
};

export const InactivityFlag = () => (
  <span className="ct-flag">
    {icon(AlertTriangle)}
    No activity in 7+ days
  </span>
);

// Illustration inside the design's "plate" frame. With `art` it shows the
// SVG, falling back to the PNG where SVG can't be shown (the browser skips a
// <source> type it can't render); without it, a labelled placeholder.
// The illustrations are decorative, so the image has empty alt text.
export const Plate = ({ label, art, className = "" }) => (
  <div className={`ct-plate ${className}`}>
    {art ? (
      <picture className="ct-plate__art">
        <source srcSet={art.svg} type="image/svg+xml" />
        <img src={art.png} alt="" />
      </picture>
    ) : (
      <PaperPlaceholder label={label} w="100%" h="100%" tone="oklch(0.85 0.05 145)" />
    )}
  </div>
);

export const SegmentedControl = ({ options, value, onChange, label }) => (
  <div className="ct-seg" role="group" aria-label={label}>
    {options.map(([key, text]) => (
      <button
        key={key}
        type="button"
        className="ct-seg__opt"
        aria-pressed={value === key}
        onClick={() => onChange(key)}
      >
        {text}
      </button>
    ))}
  </div>
);

export const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className="ct-toggle"
    onClick={() => onChange(!checked)}
  >
    <span className="ct-toggle__knob" />
  </button>
);

export const Stepper = ({ value, min, max, onChange, label }) => (
  <div className="ct-stepper">
    <button
      type="button"
      aria-label={`Lower ${label}`}
      disabled={value <= min}
      onClick={() => onChange(Math.max(min, value - 1))}
    >
      −
    </button>
    <span className="ct-stepper__value" aria-live="polite" aria-label={label}>
      {value}
    </span>
    <button
      type="button"
      aria-label={`Raise ${label}`}
      disabled={value >= max}
      onClick={() => onChange(Math.min(max, value + 1))}
    >
      +
    </button>
  </div>
);

export const Toast = ({ toast, player = false }) =>
  toast ? (
    <div
      role="status"
      className={`ct-toast${player ? " ct-toast--player" : ""}${
        toast.error ? " ct-toast--error" : ""
      }`}
    >
      {toast.error ? icon(AlertTriangle) : icon(Check)}
      {toast.text}
    </div>
  ) : null;

export const TOAST_MS = 3200;

// const [toast, showToast] = useToast(); showToast("Saved") / showToast(t, {error: true})
export const useToast = () => {
  const [toast, setToast] = useState(null);
  const timer = useRef(null);
  const showToast = useCallback((text, { error = false } = {}) => {
    clearTimeout(timer.current);
    setToast({ text, error });
    timer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [toast, showToast];
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Modal shell: traps focus, closes on Esc or a backdrop click, and hands
// focus back to whatever opened it.
export const Dialog = ({
  onClose,
  labelledBy,
  role = "dialog",
  className = "ct-dialog",
  backdropClassName = "ct-backdrop",
  children,
}) => {
  const panel = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const opener = document.activeElement;
    const node = panel.current;
    const first = node?.querySelector(FOCUSABLE);
    (first || node)?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close.current();
        return;
      }
      if (e.key !== "Tab" || !node) return;
      const items = Array.from(node.querySelectorAll(FOCUSABLE));
      if (!items.length) return;
      const head = items[0];
      const tail = items[items.length - 1];
      if (e.shiftKey && document.activeElement === head) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && document.activeElement === tail) {
        e.preventDefault();
        head.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, []);

  return (
    <div
      className={backdropClassName}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role={role}
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={className}
      >
        {children}
      </div>
    </div>
  );
};
